import React, { useEffect, useState, useCallback } from 'react'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import {
  connectGameAnalysisWebSocket,
  disconnectGameAnalysisWebSocket,
} from '@/redux/asyncThunks/gameAnalysis'
import { clearFilters } from '@/redux/slices/gameAnalysisSlice'
import FilterSelector from '@/components/ChatAnalysis/FilterSelector'
import MessagesDisplay from '@/components/ChatAnalysis/MessagesDisplay'
import SelectedFiltersDisplay from '@/components/ChatAnalysis/SelectedFiltersDisplay'
import ChatInput from '@/components/ChatAnalysis/ChatInput'
import { getGameSummary } from '@/api/room'
import { useParams } from 'react-router-dom'
import { getAccessToken } from '@/utils/authUtils'
import { GameSummaryMetadata } from '@/redux/types/room'

const GameAnalysisPanel: React.FC = ({}) => {
  const { roomId } = useParams<{ roomId: string }>()
  const { selectedFilters } = useAppSelector((state) => state.gameAnalysis)
  const dispatch = useAppDispatch()
  const jwtToken = getAccessToken()

  const [metadata, setMetadata] = useState<GameSummaryMetadata | null>(null)
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true)
  const [summaryAvailable, setSummaryAvailable] = useState(false)
  const [summaryReady, setSummaryReady] = useState(false)

  // Load metadata on mount
  useEffect(() => {
    const loadMetadata = async () => {
      if (!roomId) return
      try {
        setIsLoadingMetadata(true)
        const summary = await getGameSummary(roomId)
        setMetadata(summary.metadata)
        setSummaryAvailable(summary.summary_available)
        setSummaryReady(summary.summary_ready)
      } catch (error) {
        console.error('Failed to load game summary metadata:', error)
        setSummaryAvailable(false)
        setSummaryReady(false)
      } finally {
        setIsLoadingMetadata(false)
      }
    }
    loadMetadata()
  }, [roomId])

  // Poll for summary readiness every 5 seconds if available but not ready
  useEffect(() => {
    if (!roomId || !summaryAvailable || summaryReady) return

    const intervalId = setInterval(async () => {
      try {
        const summary = await getGameSummary(roomId)
        if (summary.summary_ready) {
          setSummaryReady(true)
          setMetadata(summary.metadata)
        }
      } catch (error) {
        // Silently fail - summary might not be ready yet
      }
    }, 5000)

    return () => clearInterval(intervalId)
  }, [roomId, summaryAvailable, summaryReady])

  useEffect(() => {
    if (roomId && jwtToken && summaryAvailable && summaryReady) {
      dispatch(connectGameAnalysisWebSocket({ roomId, jwtToken }))
    }
    return () => {
      dispatch(disconnectGameAnalysisWebSocket())
    }
  }, [roomId, dispatch, summaryAvailable, summaryReady])

  const handleClearFilters = useCallback(() => {
    dispatch(clearFilters())
  }, [dispatch])

  return (
    <div
      className={'flex flex-col h-full bg-white rounded-lg border shadow-sm'}
    >
      {summaryAvailable && (
        <div className='p-2 border-b'>
          {isLoadingMetadata ? (
            <div className='text-sm text-gray-500'>Loading filters...</div>
          ) : (
            <FilterSelector
              metadata={metadata}
              selectedFilters={selectedFilters}
              onClearFilters={handleClearFilters}
            />
          )}
        </div>
      )}

      <MessagesDisplay
        summaryAvailable={summaryAvailable}
        summaryReady={summaryReady}
        isLoadingMetadata={isLoadingMetadata}
        metadata={metadata}
      />

      {summaryAvailable && summaryReady && (
        <div className='p-2 space-y-2'>
          <SelectedFiltersDisplay
            onClearFilters={handleClearFilters}
            metadata={metadata}
          />

          <ChatInput />
        </div>
      )}
    </div>
  )
}

export default GameAnalysisPanel
